import { durationMs } from "odyssey-shared";
import { say } from "../../words.ts";

/**
 * The level-up toast (P2.4-T4, R-0074; the design's `XpBar` `levelup` state and its `Toast` in the toast column): which
 * `progress` raises it, its words, and how long it shows. A session's first `progress` only says where the character
 * stands, so it raises nothing; a later one above the level before raises one toast naming the level reached, however
 * many levels it crossed (`LevelTracker`). A level-up while the toast shows replaces its title and restarts its time.
 * The toast enters on the motion table's `toast-in`, holds for `toast-hold`, and is marked leaving so its exit, a single
 * blink of the table's `blink` (ADR-061's note of 2026-10-02), can play before it is gone; under reduced motion there is
 * no travel and no blink and the toast simply stands for the same time.
 */

/**
 * The level the toast names, if a `progress` raises one.
 *
 * @param before The level the session's last `progress` carried, null before its first.
 * @param after The level this `progress` carries.
 * @returns The level reached, or null when none was.
 * @example
 * levelReached(1, 4);
 * // 4
 */
export function levelReached(before: number | null, after: number): number | null {
  return before !== null && after > before ? after : null;
}

/**
 * The session's last level, which says whether a `progress` reached a level: a session's first says only where the
 * character stands, and a session's end forgets it, so a reconnect or the next character raises nothing.
 *
 * @example
 * ```ts
 * const tracker = new LevelTracker();
 * tracker.observe(4);
 * // null
 * tracker.observe(6);
 * // 6
 * ```
 */
export class LevelTracker {
  private last: number | null = null;

  /**
   * Takes a `progress`'s level.
   *
   * @param level The level it carries.
   * @returns The level reached, or null when none was.
   */
  public observe(level: number): number | null {
    const reached = levelReached(this.last, level);
    this.last = level;
    return reached;
  }

  /**
   * Forgets the level, as a session's end must.
   */
  public reset(): void {
    this.last = null;
  }
}

/**
 * The toast's title (draft words for the author; the design's "Level 13" with our level).
 *
 * @param level The level reached.
 * @returns The title.
 * @example
 * levelUpTitle(3);
 * // "Level 3"
 */
export function levelUpTitle(level: number): string {
  return say("hud.levelUp.title", { level: String(level) });
}

/**
 * What the toast shows: the level it names and whether it is leaving.
 */
export interface LevelUpShown {
  readonly level: number;
  readonly leaving: boolean;
}

/**
 * A timer: runs a call after a wait and returns its cancel.
 */
export type LevelUpSchedule = (fn: () => void, ms: number) => () => void;

/**
 * The toast's state and timer, which the HUD's toast column reads through `subscribe` and `getSnapshot`.
 *
 * @example
 * ```ts
 * const toast = new LevelUpToast((fn, ms) => { const t = setTimeout(fn, ms); return () => clearTimeout(t); });
 * toast.show(3);
 * toast.getSnapshot();
 * // { level: 3, leaving: false }
 * ```
 */
export class LevelUpToast {
  private shown: LevelUpShown | null = null;
  private readonly listeners = new Set<() => void>();
  private cancelLeave: (() => void) | null = null;
  private cancelExpire: (() => void) | null = null;

  /**
   * @param schedule The timer its time runs on.
   */
  public constructor(private readonly schedule: LevelUpSchedule) {}

  /**
   * Adds a listener for a changed toast.
   *
   * @param listener Called after each change.
   * @returns A function that removes it.
   */
  public readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  /**
   * What the toast shows now.
   *
   * @returns The level and whether it is leaving, or null for no toast.
   */
  public readonly getSnapshot = (): LevelUpShown | null => this.shown;

  /**
   * Shows the toast for a level, replacing any toast showing, and starts its time again.
   *
   * @param level The level reached.
   */
  public show(level: number): void {
    this.stop();
    this.set({ level, leaving: false });
    const leaveAt = durationMs("toast-in") + durationMs("toast-hold");
    this.cancelLeave = this.schedule(() => {
      this.cancelLeave = null;
      this.set({ level, leaving: true });
    }, leaveAt);
    this.cancelExpire = this.schedule(() => {
      this.cancelExpire = null;
      this.set(null);
    }, leaveAt + durationMs("blink"));
  }

  /**
   * Removes the toast at once and stops its time, as a session's end must.
   */
  public clear(): void {
    this.stop();
    if (this.shown !== null) {
      this.set(null);
    }
  }

  /**
   * Cancels the running timers.
   */
  private stop(): void {
    this.cancelLeave?.();
    this.cancelLeave = null;
    this.cancelExpire?.();
    this.cancelExpire = null;
  }

  /**
   * Replaces what the toast shows and tells the listeners.
   *
   * @param shown The new state.
   */
  private set(shown: LevelUpShown | null): void {
    this.shown = shown;
    for (const listener of this.listeners) {
      listener();
    }
  }
}
